import { BfxBlotterOrdersContextMenuItemModifyState } from "./BfxBlotterOrdersContextMenuItemModifyState";

import { BfxBlotterOrdersContextMenuState } from "./BfxBlotterOrdersContextMenuState";
import { BfxBlotterOrdersContextMenuItem } from "./BfxBlotterOrdersContextMenuItem";

import { BfxBlotterAction } from "../bfx-base-blotter/BfxBlotterAction";

import { ContextMenuItemState } from "../ContextMenuItemState";
import { ContextMenuItemStateCommand } from "../ContextMenuItemStateCommand";
import { ContextMenuItemStateCommandCriteria } from "../ContextMenuItemStateCommandCriteria";
import { ContextMenuState } from "../ContextMenuState";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { PROP_POSTHARDCUTOFF } from "shared/cubiq-services/mediator/contextmenu/bfx-blotter-orders/impl/BfxBlotterOrdersContextMenuPrimaryService";
import { ActiveUser } from "../../core/ActiveUser";

export class BfxBlotterOrdersContextMenuModifyItem extends BfxBlotterOrdersContextMenuItem {
  constructor() {
    super(
      BfxBlotterAction.ACTION_ORDER_MODIFY,
      "Modify",
      BfxBlotterOrdersContextMenuModifyItem.checkAvailableFn(),
      BfxBlotterOrdersContextMenuModifyItem.checkEnabledFn(),
      ContextMenuItemStateCommandCriteria.MenuItem,
      ContextMenuItemStateCommandCriteria.Item,
    );
  }

  public reset(): void {
    let oldState = this.state;
    this.state = new BfxBlotterOrdersContextMenuItemModifyState();
    this.state.title = oldState.title;
  }

  private static checkAvailableFn(): ContextMenuItemStateCommand<CubiqTS.OrderDataImpl> {
    return function (
      this: void,
      menuState: BfxBlotterOrdersContextMenuState,
      itemState: ContextMenuItemState,
    ) {
      itemState.available =
        (menuState.canModify ||
          menuState.canUnrestrictedCancelModify ||
          menuState.canModifyTriggered) &&
        menuState.canViewOrderEntry;
    };
  }

  private static checkEnabledFn(): ContextMenuItemStateCommand<CubiqTS.OrderDataImpl> {
    return function (
      this: void,
      menuState: BfxBlotterOrdersContextMenuState,
      itemState: BfxBlotterOrdersContextMenuItemModifyState,
      item: CubiqTS.OrderDataImpl,
    ) {
      if (!item) {
        itemState.enabled = false;
      } else if (CubiqTS.OrderState.FILL_PENDING == item.orderState) {
        itemState.enabled = false;
      } else if (
        CubiqTS.StrategyType.GAMMA == item.strategyType ||
        CubiqTS.OrderType.BMALGO == item.orderType ||
        (CubiqTS.OrderType.BENCHMARK == item.orderType &&
          item.fixingDetails?.bmOrderType === CubiqTS.BenchmarkOrderType.PositionOrder)
      ) {
        // Cannot modify gamma orders, bm algos or bmhos
        itemState.enabled = false;
      } else {
        itemState.enabled =
          itemState.enabled && (menuState.canModify || menuState.canUnrestrictedCancelModify); //.oeActions.isThereAnyReasonNotToEnableModifyOn(item);
        itemState.enabled =
          itemState.enabled &&
          BfxBlotterOrdersContextMenuModifyItem.checkSelectionCount(item, menuState);
        itemState.enabled =
          itemState.enabled && BfxBlotterOrdersContextMenuModifyItem.checkOrderInTransfer(item);
        itemState.enabled =
          itemState.enabled && BfxBlotterOrdersContextMenuModifyItem.checkOrderLock(item);

        switch (menuState.activeUser.userTypeId) {
          case CubiqTS.UserType.CLIENT:
            itemState.enabled =
              itemState.enabled &&
              BfxBlotterOrdersContextMenuModifyItem.checkModifyPermissionsForClient(
                item,
                menuState,
              );
            break;
          case CubiqTS.UserType.SALES:
            itemState.enabled =
              itemState.enabled &&
              BfxBlotterOrdersContextMenuModifyItem.checkModifyPermissionsForSales(item, menuState);
            break;
          case CubiqTS.UserType.TRADER:
            itemState.enabled =
              itemState.enabled &&
              BfxBlotterOrdersContextMenuModifyItem.checkModifyPermissionsForTrader(
                item,
                menuState,
              );
            break;
          case CubiqTS.UserType.ADMIN:
            itemState.enabled =
              itemState.enabled &&
              BfxBlotterOrdersContextMenuModifyItem.checkModifyPermissionsForAdmin(item, menuState);
            break;
          case CubiqTS.UserType.CLIENT_TRADER:
            itemState.enabled = false;
            break;
        }
        if (item.extendedInfo.has(PROP_POSTHARDCUTOFF)) {
          itemState.enabled = false;
        }
      }
    };
  }

  private static checkSelectionCount(
    item: CubiqTS.OrderDataImpl,
    menuState: BfxBlotterOrdersContextMenuState,
  ): boolean {
    let enabled: boolean = true;

    if (menuState != null && menuState.count != null && menuState.count > 1) {
      enabled = false;
    }

    return enabled;
  }

  private static checkOrderInTransfer(item: CubiqTS.OrderDataImpl): boolean {
    let enabled: boolean = true;

    if (item != null && item.targetDeskId != null && item.targetDeskId instanceof CubiqTS.Long) {
      enabled = false;
    }

    return enabled;
  }

  private static checkOrderLock(item: CubiqTS.OrderDataImpl): boolean {
    let enabled: boolean = true;

    if (item != null && item.lockType != null) {
      enabled = false;
    }

    return enabled;
  }

  /**
   * modifiable: Active, Triggered, Expiry Pending, Triggered Expiry Pending or Suspended
   * modifiable states: PENDING, SAVED, ACTIVE, SUSPENDED, EXPIRY_ACCEPTANCE_NEEDED, TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED,
   * modifiable with role (ROLE_ORDER_MODIFY_TRIGGERED): MANUAL_PRICING, TRIGGERED
   * non-modifiable: User selects orders in intervention (Modify In Pending, Modify Out Pending, Cancel Pending, Migration Acceptance Needed...),
   *
   */
  private static checkModifyPermissionsForTrader(
    item: CubiqTS.OrderDataImpl,
    menuState: BfxBlotterOrdersContextMenuState,
  ): boolean {
    let enabled: boolean = false;
    let belongsToActiveUserTradingGroup: boolean = false;
    let orderEnteredByUser: boolean = false;
    let isCreatorOfTheOrder: boolean =
      BfxBlotterOrdersContextMenuModifyItem.hasTheUserCreatedThisOrder(item, menuState);
    if (
      item.deskId != null &&
      item.deskId instanceof CubiqTS.Long &&
      menuState != null &&
      menuState.activeUser != null &&
      menuState.activeUser.tradingGroupsIds != null &&
      menuState.activeUser.tradingGroupsIds instanceof Array &&
      menuState.activeUser.tradingGroupsIds.length > 0
    ) {
      const traderGroupId: string = item.deskId.toString();
      belongsToActiveUserTradingGroup =
        menuState.activeUser.tradingGroupsIds.includes(traderGroupId);
    }

    switch (item.orderState) {
      case CubiqTS.OrderState.ACTIVE:
      case CubiqTS.OrderState.PENDING:
      case CubiqTS.OrderState.SAVED:
      case CubiqTS.OrderState.SUSPENDED:
      case CubiqTS.OrderState.EXPIRY_ACCEPTANCE_NEEDED:
      case CubiqTS.OrderState.TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED:
      case CubiqTS.OrderState.MANUAL_PRICING:
      case CubiqTS.OrderState.TRIGGERED:
      case CubiqTS.OrderState.FILLING:
        if (belongsToActiveUserTradingGroup || isCreatorOfTheOrder) {
          enabled = menuState.canModify || menuState.canUnrestrictedCancelModify;
        } else {
          enabled = menuState.canUnrestrictedCancelModify;
        }
        break;
      default:
        enabled = false;
        break;
    }
    return enabled;
  }

  private static checkModifyPermissionsForSales(
    item: CubiqTS.OrderDataImpl,
    menuState: BfxBlotterOrdersContextMenuState,
  ): boolean {
    let enabled: boolean = false;
    let belongsToActiveUserSalesGroup: boolean = false;
    let isCreatorOfTheOrder: boolean =
      BfxBlotterOrdersContextMenuModifyItem.hasTheUserCreatedThisOrder(item, menuState);

    if (
      item.salesGroupId != null &&
      item.salesGroupId instanceof CubiqTS.Long &&
      menuState != null &&
      menuState.activeUser != null &&
      menuState.activeUser.tradingGroupsIds != null &&
      menuState.activeUser.salesGroupsIds instanceof Array &&
      menuState.activeUser.salesGroupsIds.length > 0
    ) {
      const salesGroupId: string = item.salesGroupId.toString();
      belongsToActiveUserSalesGroup = menuState.activeUser.salesGroupsIds.includes(salesGroupId);
    }
    switch (item.orderState) {
      case CubiqTS.OrderState.ACTIVE:
      case CubiqTS.OrderState.PENDING:
      case CubiqTS.OrderState.SAVED:
      case CubiqTS.OrderState.SUSPENDED:
      case CubiqTS.OrderState.EXPIRY_ACCEPTANCE_NEEDED:
        if (belongsToActiveUserSalesGroup || isCreatorOfTheOrder) {
          enabled = menuState.canModify || menuState.canUnrestrictedCancelModify;
        } else {
          enabled = menuState.canUnrestrictedCancelModify;
        }
        break;
      case CubiqTS.OrderState.MANUAL_PRICING:
      case CubiqTS.OrderState.TRIGGERED:
      case CubiqTS.OrderState.FILLING:
      case CubiqTS.OrderState.TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED:
        if (belongsToActiveUserSalesGroup || isCreatorOfTheOrder) {
          enabled =
            (menuState.canModify || menuState.canUnrestrictedCancelModify) &&
            menuState.canModifyTriggered;
        } else {
          enabled = menuState.canUnrestrictedCancelModify && menuState.canModifyTriggered;
        }
        break;
      default:
        enabled = false;
        break;
    }
    return enabled;
  }

  private static checkModifyPermissionsForClient(
    item: CubiqTS.OrderDataImpl,
    menuState: BfxBlotterOrdersContextMenuState,
  ): boolean {
    let enabled: boolean = false;
    let isCreatorOfTheOrder: boolean =
      BfxBlotterOrdersContextMenuModifyItem.hasTheUserCreatedThisOrder(item, menuState);
    switch (item.orderState) {
      case CubiqTS.OrderState.ACTIVE:
      case CubiqTS.OrderState.PENDING:
      case CubiqTS.OrderState.SAVED:
      case CubiqTS.OrderState.SUSPENDED:
      case CubiqTS.OrderState.EXPIRY_ACCEPTANCE_NEEDED:
        enabled =
          menuState.canModify &&
          item.clientCode != null &&
          typeof item.clientCode === "string" &&
          menuState != null &&
          menuState.activeUser != null &&
          menuState.activeUser.clientIdSet != null &&
          menuState.activeUser.clientIdSet.has(item.clientCode);
        break;
      case CubiqTS.OrderState.MANUAL_PRICING:
      case CubiqTS.OrderState.TRIGGERED:
      case CubiqTS.OrderState.FILLING:
      case CubiqTS.OrderState.TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED:
        enabled =
          menuState.canModify &&
          menuState.canModifyTriggered &&
          ((item.clientCode != null &&
            typeof item.clientCode === "string" &&
            menuState != null &&
            menuState.activeUser != null &&
            menuState.activeUser.clientIdSet != null &&
            menuState.activeUser.clientIdSet.has(item.clientCode)) ||
            isCreatorOfTheOrder);
        break;
      default:
        enabled = false;
        break;
    }
    return enabled;
  }

  private static checkModifyPermissionsForAdmin(
    item: CubiqTS.OrderDataImpl,
    menuState: BfxBlotterOrdersContextMenuState,
  ): boolean {
    let enabled: boolean = false;
    switch (item.orderState) {
      case CubiqTS.OrderState.ACTIVE:
      case CubiqTS.OrderState.PENDING:
      case CubiqTS.OrderState.SAVED:
      case CubiqTS.OrderState.SUSPENDED:
      case CubiqTS.OrderState.EXPIRY_ACCEPTANCE_NEEDED:
        enabled = menuState.canModify || menuState.canUnrestrictedCancelModify;
        break;
      case CubiqTS.OrderState.MANUAL_PRICING:
      case CubiqTS.OrderState.FILLING:
      case CubiqTS.OrderState.TRIGGERED:
      case CubiqTS.OrderState.TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED:
        enabled =
          (menuState.canModify && menuState.canModifyTriggered) ||
          menuState.canUnrestrictedCancelModify;
        break;
      default:
        enabled = false;
        break;
    }
    return enabled;
  }

  private static hasTheUserCreatedThisOrder(
    item: CubiqTS.OrderDataImpl,
    menuState: BfxBlotterOrdersContextMenuState,
  ): boolean {
    return (
      item.entryUserCode != null &&
      typeof item.entryUserCode === "string" &&
      menuState != null &&
      menuState.activeUser != null &&
      menuState.activeUser.userCode != null &&
      menuState.activeUser.userCode === item.entryUserCode
    );
  }

  public static get state(): any {
    return new BfxBlotterOrdersContextMenuItemModifyState();
  }
}
